/**
 * Transaction Helper Functions and Constants
 * Provides constants, formatting, and visual helpers for admin transaction history.
 */

import * as colors from "../styles/colors";

//#region "TRANSACTION TYPE CONSTANTS"

/**
 * Transaction type identifiers used in API requests and responses.
 */
export const TRANSACTION_TYPES = {
  PAYMENT: "payment",
  WALLET_CREDIT: "wallet_credit",
  WALLET_DEBIT: "wallet_debit",
  TRANSFER: "transfer",
};

/**
 * Portuguese labels for transaction types.
 */
export const TRANSACTION_TYPE_LABELS = {
  payment: "Pagamento",
  wallet_credit: "Crédito Carteira",
  wallet_debit: "Débito Carteira",
  transfer: "Transferência",
};

/**
 * Transaction status identifiers.
 */
export const TRANSACTION_STATUSES = {
  APPROVED: "approved",
  PENDING: "pending",
  FAILED: "failed",
};

/**
 * Portuguese labels for transaction statuses.
 */
export const TRANSACTION_STATUS_LABELS = {
  approved: "Aprovado",
  pending: "Pendente",
  failed: "Falhou",
};

/**
 * Success status message from payment gateway.
 */
export const SUCCESS_STATUS_MESSAGE = "OK. Transaction successful. - code: 0";

//#endregion


//#region "FORMATTING FUNCTIONS"

/**
 * Formats a numeric amount to Brazilian Real currency format (R$ X.XXX,XX).
 * @param {number} amount - The amount to format
 * @returns {string} Formatted currency string
 */
export function formatTransactionAmount(amount) {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return "R$ 0,00";
  }

  const absAmount = Math.abs(amount);
  const formatted = absAmount
    .toFixed(2)
    .replace(".", ",")
    .replace(/(\d)(?=(\d{3})+,)/g, "$1.");

  return amount < 0 ? `R$ -${formatted}` : `R$ ${formatted}`;
}

/**
 * Formats a date to Brazilian date format (DD/MM/YYYY HH:MM).
 * @param {string|Date} dateValue - The date to format (ISO string or Date object)
 * @returns {string} Formatted date string
 */
export function formatTransactionDate(dateValue) {
  if (!dateValue) {
    return "";
  }

  const date = dateValue instanceof Date ? dateValue : new Date(dateValue);

  if (isNaN(date.getTime())) {
    return "";
  }

  const day = date.getDate().toString().padStart(2, "0");
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const year = date.getFullYear();
  const hours = date.getHours().toString().padStart(2, "0");
  const minutes = date.getMinutes().toString().padStart(2, "0");

  return `${day}/${month}/${year} ${hours}:${minutes}`;
}

//#endregion


//#region "VISUAL HELPERS"

/**
 * Returns the MaterialCommunityIcons name for a transaction type.
 * @param {string} transactionType - The transaction type
 * @returns {string} Icon name for MaterialCommunityIcons
 */
export function getTransactionTypeIcon(transactionType) {
  switch (transactionType) {
    case TRANSACTION_TYPES.PAYMENT:
      return "credit-card-outline";
    case TRANSACTION_TYPES.WALLET_CREDIT:
      return "wallet-plus-outline";
    case TRANSACTION_TYPES.WALLET_DEBIT:
      return "cash-minus";
    case TRANSACTION_TYPES.TRANSFER:
      return "bank-transfer";
    default:
      return "help-circle-outline";
  }
}

/**
 * Returns the color for a transaction amount based on credit/debit type.
 * Credits (wallet_credit, incoming transfers) are green.
 * Debits (wallet_debit, payment, outgoing transfers) are red.
 * @param {string} transactionType - The transaction type
 * @param {number} amount - The transaction amount (positive for credit, negative for debit)
 * @returns {string} Color hex code
 */
export function getTransactionAmountColor(transactionType, amount) {
  // For transfers, use amount sign to determine direction
  if (transactionType === TRANSACTION_TYPES.TRANSFER) {
    return amount >= 0 ? colors.SUCCESS : colors.ALERT;
  }

  // Credit transactions are green
  if (transactionType === TRANSACTION_TYPES.WALLET_CREDIT) {
    return colors.SUCCESS;
  }

  // Debit transactions (payment, wallet_debit) are red
  return colors.ALERT;
}

/**
 * Returns the color for a transaction status badge.
 * For payment types (1-4), checks if status message indicates success.
 * For admin transactions (10, 11) and transfers, always returns success (green).
 * @param {string} status - The transaction status (approved, pending, failed)
 * @param {string} statusMessage - Optional raw status message from payment gateway (only for types 1-4)
 * @param {string} transactionType - The transaction type (payment, wallet_credit, wallet_debit, transfer)
 * @returns {string} Color hex code
 */
export function getTransactionStatusColor(status, statusMessage = null, transactionType = null) {
  // Admin debit (wallet_debit) and transfers are always successful
  if (transactionType === TRANSACTION_TYPES.WALLET_DEBIT || transactionType === TRANSACTION_TYPES.TRANSFER) {
    return colors.SUCCESS;
  }

  // For payment and wallet_credit (types 1-4), check status message if present
  // If no statusMessage, it's an admin credit (type 10) - always successful
  if (statusMessage) {
    if (statusMessage === SUCCESS_STATUS_MESSAGE || statusMessage === "ADMIN_ADJUSTMENT" || statusMessage.includes("successful")) {
      return colors.SUCCESS;
    }
    // Any other message is considered a failure/issue
    return colors.ALERT;
  }

  // No statusMessage means admin transaction or transfer - always successful
  if (transactionType === TRANSACTION_TYPES.WALLET_CREDIT) {
    return colors.SUCCESS;
  }

  // Fallback to status-based coloring
  switch (status) {
    case "approved":
      return colors.SUCCESS;
    case "pending":
      return colors.WARNING;
    case "failed":
      return colors.ALERT;
    default:
      return colors.GRAY_DARK;
  }
}

/**
 * Returns a user-friendly status label.
 * For payment types (1-4), shows simplified status based on success/failure.
 * For admin transactions (10, 11) and transfers, always returns "Sucesso".
 * @param {string} status - The transaction status
 * @param {string} statusMessage - Optional raw status message (only for types 1-4)
 * @param {string} transactionType - The transaction type (payment, wallet_credit, wallet_debit, transfer)
 * @returns {string} User-friendly status label
 */
export function getTransactionStatusLabel(status, statusMessage = null, transactionType = null) {
  // Admin debit (wallet_debit) and transfers are always successful
  if (transactionType === TRANSACTION_TYPES.WALLET_DEBIT || transactionType === TRANSACTION_TYPES.TRANSFER) {
    return "Sucesso";
  }

  // For payment and wallet_credit (types 1-4), check status message if present
  // If no statusMessage, it's an admin credit (type 10) - always successful
  if (statusMessage) {
    if (statusMessage === SUCCESS_STATUS_MESSAGE || statusMessage === "ADMIN_ADJUSTMENT" || statusMessage.includes("successful")) {
      return "Sucesso";
    }
    return "Não finalizado";
  }

  // No statusMessage means admin transaction - always successful
  if (transactionType === TRANSACTION_TYPES.WALLET_CREDIT) {
    return "Sucesso";
  }

  return TRANSACTION_STATUS_LABELS[status] || status;
}

//#endregion
